import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../../../components/base/LanguageSwitch';
import { getAuthenticatedHomePath } from '../../../lib/adminAccess';

const configuredAdminEmails = String(
  import.meta.env.VITE_AERO_ADMIN_EMAILS ?? import.meta.env.VITE_PASSPORT_ADMIN_EMAILS ?? '',
);

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!supabase) return;

    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <nav className="px-3 py-3 sm:px-6 sm:py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/adb35acbf43dddb6c47392f03bc1d026/0f2f158cfd9960d53447a4fff3ef67e1.png"
                alt="Aero Destiny Travel"
                className="w-7 h-7 sm:w-10 sm:h-10 object-contain"
              />
            </div>
            <span className="max-w-[42vw] text-sm sm:max-w-none sm:text-xl font-bold text-white leading-tight">
              Aero Destiny Travel
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-[15px] font-medium text-white/90 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('header.home')}
            </button>
            <button
              onClick={() => scrollToSection('como-funciona')}
              className="text-[15px] font-medium text-white/90 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('header.howItWorks')}
            </button>
            <button
              onClick={() => scrollToSection('experiencias')}
              className="text-[15px] font-medium text-white/90 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('header.experiences')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[15px] font-medium text-white/90 hover:text-[#C9A84C] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('header.contact')}
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="hidden sm:block">
              <LanguageSwitch />
            </div>
            {session?.user ? (
              <Link
                to={getAuthenticatedHomePath(session.user, configuredAdminEmails)}
                className="px-2.5 py-2 sm:px-5 sm:py-2.5 border border-white/20 bg-white/5 text-white text-xs sm:text-[15px] font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
              >
                <span className="hidden sm:inline">{t('header.dashboard')}</span>
                <span className="sm:hidden">{t('header.account')}</span>
              </Link>
            ) : (
              <Link
                to="/app/login?next=%2Fapp%2Fdashboard"
                className="px-2.5 py-2 sm:px-5 sm:py-2.5 border border-white/20 bg-white/5 text-white text-xs sm:text-[15px] font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
              >
                {t('header.login')}
              </Link>
            )}

            <button
              onClick={() => scrollToSection('contact')}
              className="px-2.5 py-2 sm:px-6 sm:py-2.5 bg-[#C9A84C] text-[#0A1628] text-xs sm:text-[15px] font-semibold rounded-lg hover:bg-[#B89840] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('header.generateFull')}</span>
              <span className="sm:hidden">{t('header.generateShort')}</span>
            </button>
          </div>
        </div>
        <div className="mt-3 flex justify-end sm:hidden">
          <LanguageSwitch compact />
        </div>
      </nav>
    </header>
  );
}
